import { useRef, type KeyboardEvent } from 'react';
import type { SearchHasValue } from './message-search-utils';

const OPTIONS: ReadonlyArray<{ value: SearchHasValue; label: string; hint: string }> = [
  { value: null, label: 'Any', hint: 'Every message' },
  { value: 'file', label: 'Files', hint: 'Messages with an attachment' },
  { value: 'image', label: 'Images', hint: 'Messages with an image attached' },
  { value: 'link', label: 'Links', hint: 'Messages containing a link' },
];

interface SearchHasFilterProps {
  value: SearchHasValue;
  onChange: (value: SearchHasValue) => void;
}

/**
 * Segmented control for the content filter (`has:`) of a chat search. Shared by
 * both search panels, and the same WAI-ARIA radio-group pattern as
 * {@link SearchMatchModeToggle}: one tab stop, arrow keys move the selection
 * (wrapping), Home/End jump to the ends.
 */
export function SearchHasFilter({ value, onChange }: SearchHasFilterProps) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number) => {
    const wrapped = (index + OPTIONS.length) % OPTIONS.length;
    onChange(OPTIONS[wrapped].value);
    buttonRefs.current[wrapped]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        select(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        select(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        select(0);
        break;
      case 'End':
        event.preventDefault();
        select(OPTIONS.length - 1);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Has"
      className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40"
    >
      {OPTIONS.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.label}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            title={option.hint}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={[
              'px-2.5 py-1 text-xs rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              selected
                ? 'bg-background text-foreground shadow-sm font-medium'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
